import type React from "react";
import { useMemo } from "react";

import type { InputComponentProps } from "../../components/common/Input";
import {
    useGetDimensionDisable,
    useSetDimensionValue,
    useUpdateStatusOnChange,
} from "../../hooks/dimension/dimension.hooks";
import { useDimensionHydration } from "../../store/dimension.hydration";
import type {
    DimensionConfiguration,
    DimensionTemplate,
} from "../../types/dimensions";
import type { InputComponentTypes } from "../../types/inputs";

import * as InputDimensionHelpers from "./InputDimensionHelpers";
import { BasicDimensionBox } from "./common/components";

export interface InputDimensionConfiguration extends DimensionConfiguration {
    validation?: InputDimensionHelpers.InputValidationOptions;
    type: InputDimensionHelpers.InputDimensionComponentTypes;
}

export interface InputDimensionProps extends DimensionTemplate {
    configuration: InputDimensionConfiguration;
}

export const InputDimension: React.FunctionComponent<InputDimensionProps> = (
    props,
) => {
    const { id, dependencies, configuration, optional, requestParameterMap } =
        props;

    const {
        label,
        type: inputComponentType,
        validation,
        tooltip,
    } = configuration;

    const [dimensionData, setDimensionsValue] = useSetDimensionValue(
        id,
        (value) =>
            InputDimensionHelpers.buildRequestParameters(
                id,
                inputComponentType,
                requestParameterMap,
                value,
            ),
    );

    const disabled = useGetDimensionDisable(dependencies);

    const [dimensionStatus, warningMessage] = useMemo(
        () =>
            InputDimensionHelpers.processStatus(
                dimensionData.value,
                validation,
                optional,
            ),
        [dimensionData.value],
    );

    useDimensionHydration(id, dependencies, async (hydrationPayload) => {
        const payloadValues = {
            valueId: (() => {
                if (requestParameterMap) {
                    return requestParameterMap.value;
                } else {
                    return id;
                }
            })(),
        };

        // if value is set in payload use
        if (hydrationPayload[payloadValues.valueId]) {
            setDimensionsValue({
                value: InputDimensionHelpers.reverseProcessValue(
                    inputComponentType,
                    hydrationPayload[payloadValues.valueId],
                ),
            });
        } else if (optional) {
            setDimensionsValue({
                value: "",
            });
        } else {
            throw new Error("could not locate value in payload");
        }
    });

    useUpdateStatusOnChange(id, () => dimensionStatus);

    const inputComponentProps: InputComponentProps = {
        dimension: id,
        value: dimensionData.value,
        setValue: (value) => setDimensionsValue({ value }),
        disabled,
        label,
        type: inputComponentType as InputComponentTypes,
        tooltip,
        warningMessage,
        optional,
    };

    return (
        <BasicDimensionBox>
            {InputDimensionHelpers.getInputComponent(
                inputComponentProps,
                inputComponentType,
            )}
        </BasicDimensionBox>
    );
};
